/**
 * Light orientation.
 *
 * Authored lights (and their rotation keyframes and gizmo edits) describe a direction the way
 * the editor always has: a rotation whose forward (-Z) axis points where the light shines.
 * PlayCanvas directional and spot lights shine down the entity's -Y axis instead, so a light
 * entity oriented by its forward axis lit models (and cast shadows) 90 degrees away from where
 * it was pointed, and degenerately for a light pointing straight down.
 *
 * The light entity's rotation is therefore `forwardRotation * LIGHT_AXIS_OFFSET`, which turns
 * the engine's -Y onto the authored forward. Everything that reads or writes a light's
 * orientation in authored terms (keyframes, the gizmo read-back, the relighting shader) goes
 * through the helpers below.
 */
import * as pc from 'playcanvas';

/** Rotates -Y onto -Z: applied after the authored (forward) rotation. */
export const LIGHT_AXIS_OFFSET: Readonly<pc.Quat> = new pc.Quat().setFromEulerAngles(90, 0, 0);
const LIGHT_AXIS_OFFSET_INVERSE: Readonly<pc.Quat> = new pc.Quat().copy(LIGHT_AXIS_OFFSET).invert();

const _m = new pc.Mat4();
const _origin = new pc.Vec3();
const _dir = new pc.Vec3();
const _up = new pc.Vec3();
const _down = new pc.Vec3(0, -1, 0);

/** Authored (forward) rotation whose -Z axis points along `dir` (PlayCanvas world space). */
export function forwardRotationFromDirection(dir: { x: number; y: number; z: number }, out = new pc.Quat()): pc.Quat {
    _dir.set(dir.x, dir.y, dir.z);
    if (_dir.lengthSq() < 1e-12) _dir.set(0, -1, 0);
    _dir.normalize();
    // Pick an up vector that is not parallel to the direction (straight up / down lights).
    if (Math.abs(_dir.y) > 0.999) _up.set(0, 0, _dir.y > 0 ? 1 : -1);
    else _up.set(0, 1, 0);
    _m.setLookAt(_origin, _dir, _up);
    return out.setFromMat4(_m);
}

/** The light entity's rotation for an authored (forward) rotation. */
export function lightRotationFromForward(forward: pc.Quat, out = new pc.Quat()): pc.Quat {
    return out.mul2(forward, LIGHT_AXIS_OFFSET);
}

/** The authored (forward) rotation of a light entity's rotation. */
export function forwardRotationFromLight(light: pc.Quat, out = new pc.Quat()): pc.Quat {
    return out.mul2(light, LIGHT_AXIS_OFFSET_INVERSE);
}

/** The light entity's rotation for a world direction: the light shines along `dir`. */
export function lightRotationFromDirection(dir: { x: number; y: number; z: number }, out = new pc.Quat()): pc.Quat {
    return lightRotationFromForward(forwardRotationFromDirection(dir, out), out);
}

/** World direction a light entity shines along (its -Y axis). */
export function lightDirectionOf(entity: pc.Entity, out = new pc.Vec3()): pc.Vec3 {
    return entity.getRotation().transformVector(_down, out).normalize();
}
